/**
 * Auto-answer: the phone picks up inbound calls without the rep clicking Answer.
 *
 * What it is for. A setter on a headset works a list for hours; a click between every call is
 * both friction and a dropped call when they mis-aim. It also completes the conference-room
 * flow — "Create room" rings the rep's own phone, and with auto-answer on they are simply *in*
 * the room a second later instead of clicking Answer on their own call.
 *
 * Per browser, not per account. Auto-answer describes the seat, not the person: the same rep
 * wants it on at the desk with a headset and off on a laptop in a meeting, and a setting that
 * followed them to the laptop would have their machine start talking to a lead out loud. So it
 * lives in `localStorage` (origin-scoped, survives reloads) rather than in `ui_prefs` on the
 * server.
 */

import { campaignTakes } from "@/lib/acd";
import type { AgentState } from "@/lib/types";

const STORAGE_KEY = "acl.softphone.autoAnswer";

export function readAutoAnswer(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // Private mode and similar. Off is the safe default: a phone that answers by itself when
    // the rep did not ask it to is worse than one that needs a click.
    return false;
  }
}

export function writeAutoAnswer(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    /* non-fatal — the setting simply will not survive a reload */
  }
}

/** The shape this decision needs, so it can be tested without a Verto client or React. */
export interface AutoAnswerCall {
  id: string;
  direction: "inbound" | "outbound";
  state: string;
}

/**
 * Whether the phone should pick `incomingCallId` up on its own.
 *
 * The rule that is not obvious: **never while any other call exists**. Verto does not put the
 * first call on hold when a second is answered, so auto-answering alongside another leg drops
 * the rep into two live calls at once — the lead they were talking to hears the new one. A rep
 * who genuinely wants to take a second call chooses hold-&-answer, which is unaffected by this.
 *
 * "Any other call", not "any other *answered* call, deliberately. A call is only marked `active`
 * after `getUserMedia`, the SDP exchange and the awaited `verto.answer` round trip; until then
 * it reads as `ringing` or `trying`. Two calls inside that window would both have passed a
 * check for active-or-held, and both would have been answered with nothing held.
 *
 * `calls` is the client's own snapshot and already contains the incoming call — and never
 * contains an ended one, because the vendored client deletes a call from its map before it
 * emits, so "another call exists" cannot be satisfied by a call that has already finished.
 */
export function shouldAutoAnswer(
  enabled: boolean,
  incomingCallId: string,
  calls: readonly AutoAnswerCall[],
): boolean {
  if (!enabled) return false;
  const incoming = calls.find((c) => c.id === incomingCallId);
  if (!incoming || incoming.direction !== "inbound" || incoming.state !== "ringing") return false;
  return !calls.some((c) => c.id !== incomingCallId);
}

/**
 * How long a call the phone is answering by itself is shown as connecting, and kept from
 * ringing, before the phone falls back to asking. An answer takes well under a second. One that
 * is still not through by now has failed (a refused microphone) or is waiting on the rep (the
 * browser asking for the microphone), and either way they need Answer and Decline back, and a
 * call that the campaign turned out not to answer needs to ring.
 */
export const SELF_ANSWER_MS = 2_500;

/**
 * Whether the phone is answering the ringing call `callId` by itself: this browser's auto-answer
 * (`shouldAutoAnswer`), or its campaign's (`campaignTakes` in `lib/acd.ts`, which also covers the
 * moment before the rep's record has named the call).
 *
 * The phone's surfaces ask this instead of showing Answer and Decline for the moment before such
 * a call connects: they show the call itself, saying it is connecting. A rep with auto-answer on
 * was otherwise shown two buttons on every call that were gone before they could be read, let
 * alone pressed. `fresh` is "this call is still inside the moment the phone gives itself"
 * (`ringIsFresh`, below).
 *
 * Not a sound test, a number the rep dialed by hand coming back, or a supervisor's listen-in:
 * each of those has a view of its own for the same moment.
 */
export function answersItself(
  me: AgentState | null | undefined,
  calls: readonly AutoAnswerCall[],
  callId: string,
  browserAnswers: boolean,
  fresh: boolean,
): boolean {
  if (!fresh) return false;
  return (
    shouldAutoAnswer(browserAnswers, callId, calls) ||
    campaignTakes(me, calls, browserAnswers) === callId
  );
}

// --- the moment the phone gives itself, for each ringing call ------------------------------------------
//
// One clock for each ringing call, shared by every surface that asks (`useFreshRing` in
// `softphone/acd-call.tsx`). The dock, its panel, the rail and the Phone page each ask for
// themselves, and one that is drawn late (the panel opened, the page come to, while the call
// still rings) must not start the moment again and put "Connecting…" back over Answer and
// Decline. The moment starts when the first of them shows the call ringing and lasts
// `SELF_ANSWER_MS`. It is over at once when the phone's own answer failed (`selfAnswerFailed`):
// the rep has just been told to answer the call themselves, and needs the button. A call is
// forgotten when nothing shows it ringing any more.

interface Ring {
  /** The moment has passed, or the phone's own answer failed. */
  over: boolean;
  /** How many surfaces are showing the call ringing. */
  watchers: number;
  timer: ReturnType<typeof setTimeout> | null;
}

const rings = new Map<string, Ring>();
const ringWatchers = new Set<() => void>();
/** A ring that nothing came to show (its answer failed before the call was drawn) is kept for
 *  whatever draws it, up to this many. */
const RINGS_KEPT = 20;

function ringsChanged(): void {
  for (const notify of ringWatchers) notify();
}

function endMoment(callId: string): void {
  const ring = rings.get(callId);
  if (!ring || ring.over) return;
  ring.over = true;
  if (ring.timer !== null) clearTimeout(ring.timer);
  ring.timer = null;
  ringsChanged();
}

/** A surface is showing `callId` ringing. Returns what to call when it no longer does. */
export function watchRing(callId: string): () => void {
  let ring = rings.get(callId);
  if (!ring) {
    ring = { over: false, watchers: 0, timer: setTimeout(() => endMoment(callId), SELF_ANSWER_MS) };
    rings.set(callId, ring);
  }
  const mine = ring;
  mine.watchers += 1;
  let gone = false;
  return () => {
    if (gone) return;
    gone = true;
    mine.watchers -= 1;
    if (mine.watchers > 0 || rings.get(callId) !== mine) return;
    if (mine.timer !== null) clearTimeout(mine.timer);
    rings.delete(callId);
  };
}

/** Whether `callId` is still inside the moment the phone gives itself to answer it. A call that
 *  nothing has shown yet is: its moment starts with the first surface that draws it. */
export function ringIsFresh(callId: string): boolean {
  return rings.get(callId)?.over !== true;
}

/** The phone's own answer of `callId` failed: its moment is over now, not when the clock says. */
export function selfAnswerFailed(callId: string): void {
  if (!rings.has(callId)) {
    if (rings.size >= RINGS_KEPT) {
      for (const [id, ring] of rings) {
        if (ring.watchers > 0) continue;
        rings.delete(id);
        break;
      }
    }
    rings.set(callId, { over: false, watchers: 0, timer: null });
  }
  endMoment(callId);
}

export function subscribeRings(notify: () => void): () => void {
  ringWatchers.add(notify);
  return () => {
    ringWatchers.delete(notify);
  };
}

/** Forgets every ring and stops its clock. For tests. */
export function resetRings(): void {
  for (const ring of rings.values()) {
    if (ring.timer !== null) clearTimeout(ring.timer);
  }
  rings.clear();
  ringsChanged();
}

// --- what the campaign is answering, for the ringer ------------------------------------------------------
//
// The phone's sounds are the provider's (`softphone-context.tsx`), which knows nothing of the ACD;
// the campaign's auto-answer is a hook under it (`useCampaignAutoAnswer`), which does. The hook
// says here which ringing call the campaign is taking, and the provider's one effect for the
// tones reads it, the way it reads a supervisor's listen-in from `lib/monitor.ts`.

let campaignCall: string | null = null;
const campaignWatchers = new Set<() => void>();

/** The ringing call the rep's campaign is answering for them, or null. */
export function campaignAnswering(): string | null {
  return campaignCall;
}

export function setCampaignAnswering(callId: string | null): void {
  if (campaignCall === callId) return;
  campaignCall = callId;
  for (const notify of campaignWatchers) notify();
}

export function subscribeCampaignAnswering(notify: () => void): () => void {
  campaignWatchers.add(notify);
  return () => {
    campaignWatchers.delete(notify);
  };
}

/**
 * The call id out of an `incomingCall` event.
 *
 * The vendored client emits **one object** per event (`emit('incomingCall', callInfo)`), not
 * positional arguments — the same trap `remoteStreamFrom` exists for. Getting it wrong here is
 * quiet in the worst way: auto-answer would simply never fire, and nobody would know whether
 * the setting or the switch was at fault.
 */
export function incomingCallIdFrom(args: unknown[]): string | null {
  const payload = args[0];
  if (!payload || typeof payload !== "object") return null;
  const callId = (payload as { callId?: unknown }).callId;
  return typeof callId === "string" && callId ? callId : null;
}
